import React from "react";
import { ErrorMessage } from "formik";
import { TextField, Button, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppToast } from "../../components/Toast";
import AppTitleLogo from "../../assets/images/projam-icon.svg";
import "./auth.scss";

/**
 * The shared shell and controls behind every auth screen — Login, Register,
 * ForgotPassword and the Organisation trio. They differ only in the copy on
 * the stage and the fields in the form, so the chrome, the tokens and the
 * animations live here once rather than being pasted into six files.
 *
 * The controls stay MUI `styled()` rather than plain classes: emotion injects
 * its styles after the CSS bundle, so a stylesheet rule of equal specificity
 * would lose the tie against MUI's own.
 */

export const BRAND = "#00ac56";
export const BRAND_DEEP = "#059048";
export const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

export const AuthTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    background: '#f7f8fb',
    transition: `background .3s ${EASE}, box-shadow .3s ${EASE}`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e8eaf0',
      transition: `border-color .3s ${EASE}`,
    },
    '&:hover': {
      background: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cfd6e4',
      },
    },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: '0 0 0 4px rgba(0, 172, 86, 0.13)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: BRAND,
        borderWidth: '1.5px',
      },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: BRAND,
      },
    },
    '&.Mui-error.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(217, 45, 32, 0.12)',
    },
    '&.Mui-disabled': {
      background: '#f2f4f8',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: '#7a8194',
    '&.Mui-focused': {
      color: BRAND_DEEP,
    },
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: '20px',
    color: '#98a0b0',
    transition: `color .3s ${EASE}`,
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    fontSize: '12.5px',
    fontWeight: 500,
  },
}));

export const AuthPrimaryButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '14px',
  padding: theme.spacing(1.65, 3),
  fontWeight: 600,
  fontSize: '15.5px',
  letterSpacing: '0.2px',
  textTransform: 'none',
  color: '#ffffff',
  background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DEEP} 100%)`,
  boxShadow: '0 10px 22px -10px rgba(0, 172, 86, 0.85)',
  transition: `transform .3s ${EASE}, box-shadow .3s ${EASE}`,

  // sheen, swept across on hover
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-60%',
    width: '45%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.38), transparent)',
    transform: 'skewX(-18deg)',
    transition: `left .65s ${EASE}`,
  },
  '&:hover': {
    background: `linear-gradient(135deg, ${BRAND_DEEP} 0%, #03713a 100%)`,
    boxShadow: '0 16px 30px -12px rgba(0, 172, 86, 0.95)',
    transform: 'translateY(-2px)',
    '&::after': { left: '120%' },
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: '#eef1f6',
    color: '#98a0b0',
    boxShadow: 'none',
    transform: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover': { transform: 'none' },
    '&::after': { display: 'none' },
  },
}));

export const AuthSocialButton = styled(Button)(({ theme }) => ({
  borderRadius: '14px',
  padding: theme.spacing(1.4, 3),
  fontWeight: 600,
  fontSize: '14.5px',
  textTransform: 'none',
  background: '#ffffff',
  border: '1.5px solid #e8eaf0',
  color: '#344054',
  transition: `transform .3s ${EASE}, border-color .3s ${EASE}, box-shadow .3s ${EASE}`,
  '&:hover': {
    border: '1.5px solid rgba(0, 172, 86, 0.45)',
    background: '#ffffff',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 24px -14px rgba(16, 24, 40, 0.55)',
  },
  '& img': {
    width: '19px',
    height: '19px',
    marginRight: theme.spacing(1.5),
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover': { transform: 'none' },
  },
}));

/** Formik validation message, in the shared error style. */
export const AuthError = ({ name }) => (
  <ErrorMessage
    name={name}
    render={(msg) => <span className="au-field-error">{msg}</span>}
  />
);

/** Wraps a block so it fades and lifts in, `delay` seconds after the card. */
export const AuthRise = ({ delay = 0, className = "", children, ...rest }) => (
  <Box className={`au-rise ${className}`} style={{ '--au-d': `${delay}s` }} {...rest}>
    {children}
  </Box>
);


/**
 * Auth-page toaster — a thin alias for the app toast, so a validation error on
 * Login and a "Signed in" confirmation are visually the same object. The page
 * still owns the open/close state; only the appearance is shared.
 *
 * AuthLayout renders it OUTSIDE `.pj-auth`: that element is
 * `isolation: isolate`, which traps a Snackbar's z-index inside its stacking
 * context and lets the AppBar (a root-context sibling) paint straight over it.
 */
export const AuthNotice = ({
  open,
  onClose,
  severity = "error",
  autoHideDuration = 5000,
  children,
}) => (
  <AppToast
    open={open}
    onClose={onClose}
    severity={severity}
    autoHideDuration={autoHideDuration}
  >
    {children}
  </AppToast>
);

const Brand = ({ label }) => (
  <>
    <img src={AppTitleLogo} alt="App-Title-Logo" />
    <span>{label}</span>
  </>
);

/**
 * The two-panel shell: brand stage on the left, form on the right. The stage
 * is dropped below 992px, where the form takes the full card.
 */
export const AuthLayout = ({
  brand = "Projam Software",
  title,
  lead,
  points = [],
  art,
  notice,
  variant = "",
  children,
}) => (
  <>
    {/* Outside `.pj-auth` on purpose — see AuthNotice. */}
    {notice}

    <Box className="pj-auth">
      <div className="au-glow" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <div className={`au-card ${variant}`}>
        <aside className="au-stage">
          <div className="au-stage-glow" aria-hidden="true">
            <span />
            <span />
          </div>

          <div className="au-brand">
            <Brand label={brand} />
          </div>

          <h1 className="au-stage-title">{title}</h1>
          <p className="au-stage-lead">{lead}</p>

          <div className="au-art">{art}</div>

          <ul className="au-points">
            {points.map(({ icon: Icon, label }) => (
              <li key={label}>
                <Icon /> {label}
              </li>
            ))}
          </ul>
        </aside>

        <section className="au-form-side">
          <div className="au-brand au-brand--compact au-rise">
            <Brand label={brand} />
          </div>
          {children}
        </section>
      </div>
    </Box>
  </>
);

export default AuthLayout;
